import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../api/client'
import type { Role } from '../../api/types'
import { useAuth } from '../../auth/AuthProvider'
import { formatRelative } from '../../lib/format'
import { CopyField } from '../CopyField'

interface AdminUser {
	id: string
	email: string
	name: string
	role: Role
	disabled_at: string | null
	last_login_at: string | null
}

export function UsersPanel() {
	const { user: me } = useAuth()
	const client = useQueryClient()
	const users = useQuery({ queryKey: ['admin-users'], queryFn: () => api.get<AdminUser[]>('/api/admin/users') })
	const [resetLink, setResetLink] = useState<{ email: string; url: string } | null>(null)
	const refresh = () => client.invalidateQueries({ queryKey: ['admin-users'] })
	const update = useMutation({
		mutationFn: ({ id, ...body }: { id: string; role?: Role; disabled?: boolean }) =>
			api.patch(`/api/admin/users/${id}`, body),
		onSuccess: refresh,
	})
	const reset = useMutation({
		mutationFn: (u: AdminUser) =>
			api
				.post<{ url: string }>(`/api/admin/users/${u.id}/reset-link`)
				.then((r) => setResetLink({ email: u.email, url: r.url })),
	})
	const revoke = useMutation({ mutationFn: (id: string) => api.delete(`/api/admin/sessions/${id}`) })
	const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/admin/users/${id}`), onSuccess: refresh })

	return (
		<section className="card stack">
			<h2 style={{ margin: 0 }}>Users</h2>
			{update.isError && <p className="error small">{update.error.message}</p>}
			{remove.isError && <p className="error small">{remove.error.message}</p>}
			{resetLink && (
				<div>
					<p className="small" style={{ margin: '0 0 0.3rem' }}>
						Password reset link for {resetLink.email} (valid 24 hours, single use):
					</p>
					<CopyField label="Reset link" value={resetLink.url} />
				</div>
			)}
			<div className="table-scroll">
				<table>
					<thead>
						<tr>
							<th>User</th>
							<th>Role</th>
							<th>Last sign-in</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{users.data?.map((u) => (
							<tr key={u.id} style={{ opacity: u.disabled_at ? 0.6 : 1 }}>
								<td>
									<strong>{u.name}</strong>
									<div className="muted small">
										{u.email}
										{u.disabled_at && ' · disabled'}
									</div>
								</td>
								<td>
									<select
										aria-label={`Role for ${u.email}`}
										value={u.role}
										disabled={u.id === me?.id}
										onChange={(e) => update.mutate({ id: u.id, role: e.target.value as Role })}
									>
										<option value="viewer">Viewer</option>
										<option value="admin">Admin</option>
									</select>
								</td>
								<td className="muted small">{formatRelative(u.last_login_at)}</td>
								<td>
									<div className="row">
										<button
											type="button"
											disabled={u.id === me?.id}
											onClick={() => update.mutate({ id: u.id, disabled: !u.disabled_at })}
										>
											{u.disabled_at ? 'Enable' : 'Disable'}
										</button>
										<button type="button" onClick={() => reset.mutate(u)}>
											Reset link
										</button>
										<button type="button" onClick={() => revoke.mutate(u.id)}>
											Sign out everywhere
										</button>
										{/* Only an account nobody has used can go; anyone who has signed in is disabled instead. */}
										{!u.last_login_at && u.id !== me?.id && (
											<button
												type="button"
												className="danger"
												onClick={() =>
													window.confirm(`Delete ${u.email}? They’ve never signed in. This can’t be undone.`) &&
													remove.mutate(u.id)
												}
											>
												Delete
											</button>
										)}
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	)
}
